import { type JSX, useState, useRef, useEffect, useContext } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import supabase from '../../supabase';
import { AVATAR_STORAGE_BUCKET } from '../../config';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { SessionContext } from '../../context/SessionContext';
import { cn } from '@/lib/utils';

export interface EditProfileForm {
  avatar_url: string | null;
  username: string;
  description: string | null;
  phone_number: string | null;
}

interface EditProfileSettingsProps {
  onSave: (profile: EditProfileForm) => void;
  onCancel: () => void;
  setAvatarFile: Dispatch<SetStateAction<File | null>>;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
): Crop {
  return centerCrop(
    makeAspectCrop(
      { unit: '%', width: 90 },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

function normalizeUsername(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

async function isUsernameAvailable(username: string): Promise<boolean> {
  const { data, error } = await supabase.functions.invoke<{
    success?: boolean;
    is_available?: boolean;
  }>('check-username-availability', {
    body: { username },
  });

  if (error) {
    throw error;
  }

  return !!data?.is_available;
}

async function getCroppedImg(
  image: HTMLImageElement,
  crop: PixelCrop,
  fileName = "croppedImage.png",
  isCircular = false,
): Promise<File | null> {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  if (isCircular) {
    ctx.beginPath();
    ctx.arc(crop.width / 2, crop.height / 2, Math.min(crop.width, crop.height) / 2, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.clip();
  }

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'));
        return;
      }
      resolve(new File([blob], fileName, { type: 'image/png' }));
    }, 'image/png', 1);
  });
}

const defaultProfileData: EditProfileForm = {
  avatar_url: null,
  username: '',
  description: null,
  phone_number: null
};

export default function EditProfileSettingsWrapper({
  onSave,
  onCancel,
  setAvatarFile,
}: EditProfileSettingsProps): JSX.Element {
  const { session, profile: contextProfile, refreshProfile } = useContext(SessionContext);
  const avatarFileRef = useRef<File | null>(null);
  const [profile, setProfile] = useState<EditProfileForm>(() => {
    const initial = contextProfile || defaultProfileData;
    return {
      ...defaultProfileData,
      ...initial,
      username: initial.username || ''
    };
  });

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (session?.user?.id && !contextProfile) {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (data) {
          setProfile({
            avatar_url: data.avatar_url,
            username: data.username || '',
            description: data.description,
            phone_number: data.phone_number
          });
        }
      }
    };

    fetchProfile();
    //console.log('session:', JSON.stringify(session));
    //console.log('contextProfile:', JSON.stringify(contextProfile));
  }, [session, contextProfile]);

  const [imageSrcForCropper, setImageSrcForCropper] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const [showCropperModal, setShowCropperModal] = useState(false);
  const [croppedImagePreviewUrl, setCroppedImagePreviewUrl] = useState<string | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<{
    state: 'idle' | 'checking' | 'available' | 'taken';
    message: string | null;
  }>({
    state: 'idle',
    message: null,
  });

  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const currentPreviewUrl = croppedImagePreviewUrl;
    return () => {
      if (currentPreviewUrl) URL.revokeObjectURL(currentPreviewUrl);
    };
  }, [croppedImagePreviewUrl]);

  const handleInputChange = (field: keyof EditProfileForm, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));

    if (field === 'username') {
      setUsernameStatus({
        state: 'idle',
        message: null,
      });
    }
  };

  const checkUsernameAvailability = async () => {
    const trimmedUsername = profile.username.trim();
    const currentUserId = session?.user?.id;

    if (!trimmedUsername || !currentUserId) {
      setUsernameStatus({
        state: 'idle',
        message: null,
      });
      return;
    }

    if (trimmedUsername === (contextProfile?.username || '').trim()) {
      setUsernameStatus({
        state: 'idle',
        message: null,
      });
      return;
    }

    setUsernameStatus({
      state: 'checking',
      message: 'Checking username...',
    });

    try {
      const isAvailable = await isUsernameAvailable(trimmedUsername);
      setUsernameStatus({
        state: isAvailable ? 'available' : 'taken',
        message: isAvailable ? 'Username is available.' : 'That username is already taken.',
      });
    } catch (error) {
      console.error('Failed to check username availability:', error);
      setUsernameStatus({
        state: 'idle',
        message: null,
      });
    }
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrcForCropper(reader.result?.toString() || '');
        setShowCropperModal(true);
      });
      reader.readAsDataURL(file);
      e.target.value = "";
    }
  };

  const handleImageLoadInCropper = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth && naturalHeight) {
      setCrop(centerAspectCrop(naturalWidth, naturalHeight, 1));
    }
  };

  const handleCropConfirm = async () => {
    if (completedCrop && imgRef.current) {
      try {
        const croppedFile = await getCroppedImg(
          imgRef.current,
          completedCrop,
          "avatar.png",
          true
        );
        if (croppedFile) {
          avatarFileRef.current = croppedFile;
          setAvatarFile(croppedFile); // Update parent state
          if (croppedImagePreviewUrl) URL.revokeObjectURL(croppedImagePreviewUrl);
          const newPreviewUrl = URL.createObjectURL(croppedFile);
          setCroppedImagePreviewUrl(newPreviewUrl);
          setProfile(prev => ({ ...prev, avatar_url: newPreviewUrl }));
          console.log('croppedFile:', croppedFile);
          console.log('newPreviewUrl:', newPreviewUrl);
          console.log('profile.avatar_url:', profile.avatar_url);
        }
      } catch (e) {
        console.error("Error cropping image:", e);
      }
    }
    setShowCropperModal(false);
  };

  const handleCropCancel = () => setShowCropperModal(false);

  const handleSaveForm = async () => {
    // Add haptic feedback on save button click
    if (navigator.vibrate) {
      navigator.vibrate(150);
    }
    
    try {
      let updatedProfile = { ...profile };
      const trimmedUsername = updatedProfile.username.trim();

      if (!trimmedUsername) {
        alert('Username is required.');
        return;
      }

      if (usernameStatus.state === 'taken') {
        alert('That username is already taken.');
        return;
      }

      if (trimmedUsername !== (contextProfile?.username || '').trim()) {
        const available = await isUsernameAvailable(trimmedUsername);

        if (!available) {
          setUsernameStatus({
            state: 'taken',
            message: 'That username is already taken.',
          });
          alert('That username is already taken.');
          return;
        }
      }

      updatedProfile = {
        ...updatedProfile,
        username: trimmedUsername,
      };

      console.log('updatedProfile before update:', JSON.stringify(updatedProfile));
console.log('croppedImagePreviewUrl:', croppedImagePreviewUrl);
console.log('fileInputRef:', JSON.stringify(fileInputRef.current?.files?.[0]));

      if (avatarFileRef.current) {
        const file = avatarFileRef.current;
        if (!session?.user?.id) {
          throw new Error('User must be logged in to update avatar');
        }
        
        // Get file extension from cropped file
        const fileExt = file.name.split('.').pop() || 'png';
        const fileName = `${session.user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from(AVATAR_STORAGE_BUCKET)
          .upload(fileName, file, { cacheControl: '3600', upsert: true });

        if (uploadError) {
          alert(`Error uploading image: ${uploadError.message}`);
          throw uploadError;
        }

        const { data: urlData } = supabase.storage.from(AVATAR_STORAGE_BUCKET).getPublicUrl(fileName)
        console.log('fileName:', fileName);
        console.log('urlData.publicUrl:', urlData.publicUrl);
        updatedProfile = { ...updatedProfile, avatar_url: urlData.publicUrl };
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          username: updatedProfile.username,
          description: updatedProfile.description,
          phone_number: updatedProfile.phone_number,
          avatar_url: updatedProfile.avatar_url
        })
        .eq('id', session?.user?.id);

      if (profileError) throw profileError;

      // Refresh session context to ensure latest avatar URL is loaded
      await refreshProfile();
      
      onSave(updatedProfile);
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert(`Error saving profile changes: ${(error as Error).message}`);
    }
  };

  const handleCancelForm = () => {
    setProfile(defaultProfileData);
    if (croppedImagePreviewUrl) URL.revokeObjectURL(croppedImagePreviewUrl);
    setCroppedImagePreviewUrl(null);
    setAvatarFile(null);
    setAvatarFile(null);
    setImageSrcForCropper(null);
    setCrop(undefined);
    setCompletedCrop(null);
    setShowCropperModal(false);
    onCancel();
  };

  const displayAvatarSrc = croppedImagePreviewUrl || profile.avatar_url || '/defaultAvatar.png';
  const avatarFallbackText = profile.username.substring(0, 2).toUpperCase() || "AA";

  return (
    <div className="jr-card mt-4 space-y-6 p-6">
      <div className="w-full flex flex-col items-center mb-4 space-y-2">
        <button
          type="button"
          className="group relative flex h-32 w-32 items-center justify-center rounded-full border-2 border-[color:var(--jr-border)] bg-[color:var(--jr-surface)] transition-colors hover:border-[color:var(--jr-border-strong)]"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Change profile photo"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          tabIndex={0}
        >
          <Avatar className="jr-avatar-ring h-full w-full">
            <AvatarImage src={displayAvatarSrc} alt="User avatar" className="object-cover" />
            <AvatarFallback className="flex h-full w-full items-center justify-center rounded-full bg-[color:var(--jr-surface-muted)] text-3xl text-[color:var(--jr-accent)]">
              {avatarFallbackText}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-xs font-medium text-white bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Change Photo
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={fileInputRef}
            onChange={onFileSelect}
          />
        </button>
      </div>

      <div>
        <Label htmlFor="username" className="font-medium text-[var(--jr-text)]">Username</Label>
        <Input
          id="username"
          value={profile.username}
          onChange={(e) => handleInputChange('username', e.target.value)}
          onBlur={() => void checkUsernameAvailability()}
          className={cn(
            'jr-input mt-1',
            usernameStatus.state === 'taken' && 'border-red-500 focus-visible:ring-red-200',
            usernameStatus.state === 'available' && 'border-emerald-500 focus-visible:ring-emerald-200'
          )}
        />
        {usernameStatus.message && (
          <p
            className={cn(
              'mt-1 text-xs',
              usernameStatus.state === 'taken' && 'text-[color:#efc2bd]',
              usernameStatus.state === 'available' && 'text-[color:#dbe6d0]',
              usernameStatus.state === 'checking' && 'text-[var(--jr-text-muted)]'
            )}
          >
            {usernameStatus.message}
          </p>
        )}
      </div>
      <div>
        <Label htmlFor="description" className="font-medium text-[var(--jr-text)]">Description</Label>
        <Input id="description" value={profile.description || ''} onChange={(e) => handleInputChange('description', e.target.value)} className="jr-input mt-1" />
      </div>

      <h2 className="mt-4 border-t border-[color:var(--jr-border)] pt-4 text-lg font-semibold text-[var(--jr-text)]">Private Contact Settings</h2>
      <p className="-mt-4 text-sm text-[var(--jr-text-muted)]">These details are not shown to other users.</p>
      <div>
        <Label htmlFor="phone-edit" className="font-medium text-[var(--jr-text)]">Phone Number</Label>
        <Input id="phone-edit" type="tel" value={profile.phone_number || ''} onChange={(e) => handleInputChange('phone_number', e.target.value)} className="jr-input mt-1" />
      </div>
      <div>
        <Label htmlFor="email-edit" className="font-medium text-[var(--jr-text)]">Email Address</Label>
        <Input
          id="email-edit"
          type="email"
          value={session?.user?.email || ''}
          readOnly
          className="jr-input mt-1 cursor-not-allowed opacity-70"
          aria-readonly="true"
        />
      </div>

      <div className="mt-6 flex space-x-4 border-t border-[color:var(--jr-border)] pt-6">
        <Button
          onClick={handleSaveForm}
          className="jr-action-primary h-auto flex-1 min-h-11"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (e.key === ' ') e.preventDefault();
              handleSaveForm();
            }
          }}
          tabIndex={0}
        >
          Save Changes
        </Button>
        <Button
          onClick={handleCancelForm}
          variant="outline"
          className="jr-action-secondary h-auto flex-1 min-h-11"
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (e.key === ' ') e.preventDefault();
              handleCancelForm();
            }
          }}
        >
          Cancel
        </Button>
      </div>

      {showCropperModal && imageSrcForCropper && (
        <dialog
          open={showCropperModal}
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={handleCropCancel}
          onKeyDown={(e) => {
            if (['Escape', 'Enter', ' '].includes(e.key)) {
              e.preventDefault();
              handleCropCancel();
            }
          }}
          onKeyUp={(e) => e.key === ' ' && e.preventDefault()}
          aria-labelledby="crop-modal-heading"
          aria-modal="true"
        >
          <div
            className="jr-card w-full max-w-md rounded-[24px] p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
            onKeyDown={(e) => e.key === 'Escape' && handleCropCancel()}
          >
            <h3 id="crop-modal-heading" className="jr-display mb-4 text-center text-3xl font-semibold">Crop Your Image</h3>
            <div className="flex justify-center mb-4">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop={true}
                minWidth={50}
                minHeight={50}
                className="max-w-full"
              >
                <img
                  ref={imgRef}
                  src={imageSrcForCropper}
                  alt="Crop preview"
                  style={{ maxHeight: '60vh', objectFit: 'contain' }}
                  onLoad={handleImageLoadInCropper}
                />
              </ReactCrop>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={handleCropCancel}
                className="jr-action-secondary"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (e.key === ' ') e.preventDefault();
                    handleCropCancel();
                  }
                }}
                tabIndex={0}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCropConfirm}
                disabled={!completedCrop}
                className="jr-action-primary"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (e.key === ' ') e.preventDefault();
                    handleCropConfirm();
                  }
                }}
                tabIndex={0}
              >
                Apply Crop
              </Button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
}
